import { useState, useCallback, useRef } from 'react';
import { isNoCreditsError, uploadCandiBankSingleFile } from '../../../utils/api_service/candi_bank_api';
import { FeatureCredits } from '../../../utils/types/credits_types';

export type UploadFileStatus = 'queued' | 'uploading' | 'completed' | 'failed' | 'duplicate' | 'blocked';

export interface CandiBankUploadItem {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  status: UploadFileStatus;
  progress: number;
  error?: string;
  uploadedAt?: Date;
}

export interface CandiBankUploadCallbacks {
  /** Each successful upload returns the live credits block. */
  onCredits?: (credits: FeatureCredits) => void;
  /** The month's allowance ran out mid-batch; fired once per batch. */
  onBlocked?: (credits?: FeatureCredits) => void;
}

const CONCURRENCY_LIMIT = 3; // Upload up to 3 files concurrently
const BLOCKED_MESSAGE = 'Monthly CV credit limit reached';

export const useCandiBankUpload = (
  onUploadCompleteSuccess?: () => void,
  callbacks: CandiBankUploadCallbacks = {},
) => {
  const [items, setItems] = useState<CandiBankUploadItem[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showWidget, setShowWidget] = useState(false);
  const [isWidgetMinimized, setIsWidgetMinimized] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const activeUploadsRef = useRef(false);
  // addFilesToQueue/retryItem are memoised once, so processQueue only ever sees the first
  // render's props. Both callbacks go through refs so the completion hook fires the CURRENT
  // refresh (with the live search/page), not the one captured on mount.
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;
  const onCompleteRef = useRef(onUploadCompleteSuccess);
  onCompleteRef.current = onUploadCompleteSuccess;

  // Helper to format file size cleanly
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Add files to queue and start batch processing
  const addFilesToQueue = useCallback((fileList: FileList | File[]) => {
    const filesArray = Array.from(fileList);
    if (filesArray.length === 0) return;

    const newItems: CandiBankUploadItem[] = filesArray.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      file,
      name: file.name,
      size: file.size,
      type: file.type || file.name.split('.').pop() || 'file',
      status: 'queued',
      progress: 0,
    }));

    setItems((prev) => [...newItems, ...prev]);
    setShowUploadModal(false);
    setShowWidget(true);
    setIsWidgetMinimized(false);

    // Trigger queue runner asynchronously
    setTimeout(() => {
      processQueue();
    }, 100);
  }, []);

  // Process queued items with pool concurrency
  const processQueue = async () => {
    if (activeUploadsRef.current) return;
    activeUploadsRef.current = true;
    setIsUploading(true);
    let blocked = false;
    let blockedCredits: FeatureCredits | undefined;

    try {
      let hasMoreToProcess = true;
      while (hasMoreToProcess) {
        let currentQueuedItems: CandiBankUploadItem[] = [];
        setItems((prev) => {
          currentQueuedItems = prev.filter((i) => i.status === 'queued');
          return prev;
        });

        await new Promise((res) => setTimeout(res, 50));

        if (currentQueuedItems.length === 0) {
          hasMoreToProcess = false;
          break;
        }

        // Take up to CONCURRENCY_LIMIT items to process concurrently
        const batch = currentQueuedItems.slice(0, CONCURRENCY_LIMIT);
        const batchIds = batch.map((item) => item.id);

        // Set status to uploading
        setItems((prev) =>
          prev.map((item) =>
            batchIds.includes(item.id)
              ? { ...item, status: 'uploading', progress: 10 }
              : item
          )
        );

        // Upload each file individually
        await Promise.all(
          batch.map(async (item) => {
            try {
              const res = await uploadCandiBankSingleFile(item.file, (progressPercent) => {
                setItems((prev) =>
                  prev.map((i) =>
                    i.id === item.id
                      ? { ...i, progress: Math.max(15, progressPercent) }
                      : i
                  )
                );
              });

              if (res.credits) {
                callbacksRef.current.onCredits?.(res.credits);
              }

              // Mark individual item as COMPLETED
              setItems((prev) =>
                prev.map((i) =>
                  i.id === item.id
                    ? {
                        ...i,
                        status: 'completed',
                        progress: 100,
                        uploadedAt: new Date(),
                        error: undefined,
                      }
                    : i
                )
              );
            } catch (err: any) {
              if (isNoCreditsError(err)) {
                blocked = true;
                blockedCredits = err.credits ?? blockedCredits;
                setItems((prev) =>
                  prev.map((i) =>
                    i.id === item.id
                      ? { ...i, status: 'blocked', progress: 0, error: BLOCKED_MESSAGE }
                      : i
                  )
                );
                return;
              }

              const errMsg = err?.message || 'Server Error';
              const isDup = errMsg.toLowerCase().includes('duplicate') || errMsg.toLowerCase().includes('already exists');

              // Mark item as DUPLICATE or FAILED
              setItems((prev) =>
                prev.map((i) =>
                  i.id === item.id
                    ? {
                        ...i,
                        status: isDup ? 'duplicate' : 'failed',
                        progress: 0,
                        error: isDup ? 'Duplicate CV — Already exists in Candi Bank' : errMsg,
                      }
                    : i
                )
              );
            }
          })
        );

        if (blocked) {
          // Nothing queued behind this batch can succeed this month: stop instead of
          // firing one refused request per file.
          setItems((prev) =>
            prev.map((i) =>
              i.status === 'queued' ? { ...i, status: 'blocked', progress: 0, error: BLOCKED_MESSAGE } : i
            )
          );
          break;
        }
      }

      if (blocked) {
        callbacksRef.current.onBlocked?.(blockedCredits);
        if (blockedCredits) callbacksRef.current.onCredits?.(blockedCredits);
      }

      onCompleteRef.current?.();
    } finally {
      activeUploadsRef.current = false;
      setIsUploading(false);
    }
  };

  // Retry a failed (or blocked, once credits reset) item
  const retryItem = useCallback((id: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, status: 'queued', progress: 0, error: undefined } : item
      )
    );
    setTimeout(() => {
      processQueue();
    }, 100);
  }, []);

  // Remove / Cancel an item
  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Clear settled uploads: done, duplicate, and blocked (a blocked row can also be retried).
  const clearCompleted = useCallback(() => {
    setItems((prev) =>
      prev.filter((item) => item.status !== 'completed' && item.status !== 'duplicate' && item.status !== 'blocked')
    );
  }, []);

  // Stats calculation
  const totalCount = items.length;
  const completedCount = items.filter((i) => i.status === 'completed').length;
  const duplicateCount = items.filter((i) => i.status === 'duplicate').length;
  const failedCount = items.filter((i) => i.status === 'failed').length;
  const blockedCount = items.filter((i) => i.status === 'blocked').length;
  const uploadingCount = items.filter((i) => i.status === 'uploading' || i.status === 'queued').length;

  const overallProgress =
    totalCount > 0
      ? Math.round(
          items.reduce((acc, curr) => acc + (curr.status === 'completed' || curr.status === 'duplicate' ? 100 : curr.progress || 0), 0) / totalCount
        )
      : 0;

  return {
    items,
    showUploadModal,
    setShowUploadModal,
    showWidget,
    setShowWidget,
    isWidgetMinimized,
    setIsWidgetMinimized,
    isUploading,
    addFilesToQueue,
    retryItem,
    removeItem,
    clearCompleted,
    formatFileSize,
    totalCount,
    completedCount,
    duplicateCount,
    failedCount,
    blockedCount,
    uploadingCount,
    overallProgress,
  };
};
